import {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
  useMemo,
} from 'react';
import { StockLotCRUDSchema, StockEventType } from 'types/inventory';
import {
  StockLocationSchema,
  StockLocationSchemaLite,
} from 'types/clientV2/stockLocations';
import {
  useCounterSerialMessages,
  UseCounterSerialMessagesReturn,
} from 'views/warehouse/Counting/hooks/useCounterSerialMessages';
import { useScannerContext } from 'contexts/ScannerContext';
import { useCreateStockEventsMutation } from 'store/slices/clientV2/stockEvents';
import {
  useUpdateStockLotMutation,
  usePartialSplitStockLotPurchaseLineMutation,
} from 'store/slices/clientV2/stockLots';
import { useTransferStockLot } from 'views/warehouse/hooks/useTransferStockLot';
import useSnackbar from 'hooks/useSnackbar';
import { handleErr } from 'utils/functions';
import { useCountingValidation } from 'views/warehouse/Counting/hooks/useCountingValidation';
import { useLazyGetStockLocationsClientV2Query } from 'store/slices/clientV2/stockLocations';
import { useServerSideLazyQuery } from 'hooks/useServerSideLazyQuery';
import {
  isReceivedFromIngest,
  isReceivedFromPurchaseLine,
  getHandlingRequestInfo,
} from 'views/warehouse/Counting/utils';
import {
  ValidationWithStatus,
  ValidationParams,
} from 'views/warehouse/Counting/hooks/constants';
import { ALL, QueryFilterOperators } from 'types/api';

export type StockLotWithCount = {
  stockLot: StockLotCRUDSchema;
  countedQuant: number | undefined;
  previousQuant: number;
  station: string | undefined;
  complete: boolean;
  receivedFromIngest: boolean;
  receivedFromPurchaseLine: boolean;
  validation?: ValidationWithStatus;
};

type HandleApproveStockLotParams = {
  stockLotId: string;
  quarantined: boolean;
  quant: number;
  smtNoPnp: boolean;
  tapeSize: number | undefined;
  transferToLocation?: string;
  splitPurchaseLine?: boolean;
};

interface CountingContextType {
  countedStockLots: StockLotWithCount[];
  storeToLocation: StockLocationSchemaLite | null;
  auditMode: boolean;
  isLoading: boolean;
  serial: UseCounterSerialMessagesReturn['serial'];
  messageHistory: UseCounterSerialMessagesReturn['messageHistory'];
  clearMessageHistory: UseCounterSerialMessagesReturn['clearMessageHistory'];

  completedStockLots: StockLotWithCount[];
  uncompletedStockLots: StockLotWithCount[];

  unresolvedValidations: ValidationWithStatus[];
  resolveValidation: (stockLotId: string) => ValidationWithStatus | undefined;
  deleteValidation: (stockLotId: string) => void;
  handleValidateCountedStockLot: (
    params: ValidationParams
  ) => ValidationWithStatus | null;
  validateAndUpdateStockLot: (
    params: ValidationParams
  ) => ValidationWithStatus | null;
  handleUnCompleteStockLot: (stockLotId: string) => void;
  handleDismissStockLot: (stockLotId: string) => void;
  handleResetCounts: () => void;
  toggleAuditMode: () => void;
  handleApproveStockLot: (params: HandleApproveStockLotParams) => Promise<void>;
  handleUpdateLocation: (stockLot: StockLotCRUDSchema) => Promise<void>;
}

const CountingContext = createContext<CountingContextType | undefined>(
  undefined
);

interface CountingProviderProps {
  children: ReactNode;
}

export const CountingProvider = ({ children }: CountingProviderProps) => {
  const [countedStockLots, setCountedStockLots] = useState<StockLotWithCount[]>(
    []
  );
  const [storeToLocation, setStoreToLocation] =
    useState<StockLocationSchemaLite | null>(null);
  const [auditMode, setAuditMode] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    serial,
    getMessageForLotId,
    clearAllMessages,
    messageHistory,
    clearMessageHistory,
  } = useCounterSerialMessages();

  const {
    handleValidateCountedStockLot,
    resolveValidation: resolveValidationInHook,
    deleteValidation,
    unresolvedValidations,
  } = useCountingValidation();
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();

  const [createStockEvents] = useCreateStockEventsMutation();
  const [updateStockLot] = useUpdateStockLotMutation();
  const [getStockLocations] = useServerSideLazyQuery<
    StockLocationSchema,
    StockLocationSchemaLite
  >(useLazyGetStockLocationsClientV2Query);
  const { handleTransferStockLot } = useTransferStockLot();
  const [partialSplitStockLotPurchaseLine] =
    usePartialSplitStockLotPurchaseLineMutation();

  const handleScannedStockLot = useCallback(
    (stockLot: StockLotCRUDSchema) => {
      const message = getMessageForLotId(
        `${stockLot.printedPart}${stockLot.lotId}`
      );

      const receivedFromIngest = isReceivedFromIngest(stockLot);
      const receivedFromPurchaseLine = isReceivedFromPurchaseLine(stockLot);

      const { isFromHandlingRequest, handlingRequestType } =
        getHandlingRequestInfo(stockLot);

      let validationResult: ValidationWithStatus | null = null;
      if (message) {
        validationResult = handleValidateCountedStockLot({
          countedQuant: message.quant,
          expectedQuant: stockLot.quant,
          stockLotId: stockLot.id,
          isFromReceive: receivedFromPurchaseLine,
          isFromHandlingRequest,
          handlingRequestType,
          package: stockLot.package,
          tapeSize: stockLot.tapeSize,
        });
      }

      const newStockLot: StockLotWithCount = {
        stockLot,
        countedQuant: message?.quant ?? stockLot.quant,
        previousQuant: stockLot.quant,
        station: message?.station,
        complete: false,
        receivedFromIngest,
        receivedFromPurchaseLine,
        validation: validationResult || undefined,
      };

      setCountedStockLots((prev) => [...prev, newStockLot]);

      return Promise.resolve(true);
    },
    [getMessageForLotId, handleValidateCountedStockLot]
  );

  const handleScannedStockLocation = useCallback(
    (scannedStockLocation: StockLocationSchemaLite) => {
      setStoreToLocation(scannedStockLocation);
      dispatchSuccessSnackbar('Stock location updated');
    },
    [dispatchSuccessSnackbar]
  );

  useScannerContext({
    handleScannedStockLot,
    handleScannedStockLocation,
  });

  const completedStockLots = useMemo(
    () => countedStockLots.filter((lot) => lot.complete).reverse(),
    [countedStockLots]
  );
  const uncompletedStockLots = useMemo(
    () => countedStockLots.filter((lot) => !lot.complete),
    [countedStockLots]
  );

  const handleUnCompleteStockLot = useCallback((stockLotId: string) => {
    setCountedStockLots((prev) =>
      prev.map((item) =>
        item.stockLot.id === stockLotId ? { ...item, complete: false } : item
      )
    );
  }, []);

  const handleDismissStockLot = useCallback(
    (stockLotId: string) => {
      deleteValidation(stockLotId);
      setCountedStockLots((prev) =>
        prev.filter((item) => item.stockLot.id !== stockLotId)
      );
    },
    [deleteValidation]
  );

  const handleResetCounts = useCallback(() => {
    setCountedStockLots([]);
    clearAllMessages();
  }, [clearAllMessages]);

  const toggleAuditMode = useCallback(() => {
    setAuditMode((prev) => !prev);
  }, []);

  const handleApproveStockLot = useCallback(
    async ({
      stockLotId,
      quant,
      smtNoPnp,
      tapeSize,
      transferToLocation,
      splitPurchaseLine,
    }: HandleApproveStockLotParams) => {
      setIsLoading(true);
      try {
        const stockLot = countedStockLots.find(
          (item) => item.stockLot.id === stockLotId
        )?.stockLot;

        if (!stockLot) {
          dispatchErrorSnackbar('Stock lot not found');
          setIsLoading(false);
          return;
        }

        if (splitPurchaseLine && stockLot.purchaseLine) {
          await partialSplitStockLotPurchaseLine({
            id: stockLot.id,
            payload: {
              quant,
              purchaseLine: stockLot.purchaseLine,
            },
          }).unwrap();
        }

        await createStockEvents({
          eventType: StockEventType.ADJUST_QUANT,
          stockLot: stockLot.id,
          quant,
        }).unwrap();

        await updateStockLot({
          id: stockLot.id,
          payload: {
            smtNoPnp: smtNoPnp || false,
            ...(tapeSize !== undefined && { tapeSize }),
            ...(auditMode ? { auditedAt: new Date().toISOString() } : {}),
          },
        }).unwrap();

        const targetLocationId =
          transferToLocation || (storeToLocation?.id ?? null);
        if (targetLocationId) {
          try {
            const { data: locationResponse } = transferToLocation
              ? await getStockLocations({
                  pageSize: 1,
                  schema: [ALL],
                  filters: [
                    {
                      field: 'id',
                      operator: QueryFilterOperators['='],
                      value: transferToLocation,
                    },
                  ],
                }).unwrap()
              : { data: [] };

            const targetLocation = transferToLocation
              ? locationResponse?.[0]
              : storeToLocation;

            if (!targetLocation) {
              throw new Error('Target location not found');
            }

            await handleTransferStockLot(stockLot, targetLocation);
          } catch (err) {
            dispatchErrorSnackbar('Failed to transfer stock lot');
            setIsLoading(false);
            return;
          }
        }

        setCountedStockLots((prev) =>
          prev.map((item) =>
            item.stockLot.id === stockLotId
              ? {
                  ...item,
                  complete: true,
                  countedQuant: quant,
                  stockLot: {
                    ...item.stockLot,
                    smtNoPnp: smtNoPnp || false,
                  },
                }
              : item
          )
        );

        dispatchSuccessSnackbar('Stock lot processed successfully');
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      countedStockLots,
      getStockLocations,
      handleTransferStockLot,
      storeToLocation,
      auditMode,
      updateStockLot,
      dispatchSuccessSnackbar,
      dispatchErrorSnackbar,
      createStockEvents,
      partialSplitStockLotPurchaseLine,
    ]
  );

  const handleUpdateLocation = useCallback(
    async (stockLot: StockLotCRUDSchema) => {
      if (!storeToLocation) {
        dispatchErrorSnackbar('No location selected');
        return;
      }

      setIsLoading(true);
      try {
        await handleTransferStockLot(stockLot, storeToLocation);
        dispatchSuccessSnackbar('Stock lot moved successfully');
      } catch (err) {
        dispatchErrorSnackbar('Failed to move stock lot');
      } finally {
        setIsLoading(false);
      }
    },
    [
      storeToLocation,
      handleTransferStockLot,
      dispatchSuccessSnackbar,
      dispatchErrorSnackbar,
    ]
  );

  const resolveValidation = useCallback(
    (stockLotId: string): ValidationWithStatus | undefined => {
      const updatedValidation = resolveValidationInHook(stockLotId);

      if (updatedValidation) {
        setCountedStockLots((prev) =>
          prev.map((item) =>
            item.stockLot.id === stockLotId && item.validation
              ? { ...item, validation: updatedValidation }
              : item
          )
        );
      }

      return updatedValidation;
    },
    [resolveValidationInHook]
  );

  const validateAndUpdateStockLot = useCallback(
    (params: ValidationParams): ValidationWithStatus | null => {
      const validationResult = handleValidateCountedStockLot(params);

      if (validationResult) {
        setCountedStockLots((prev) =>
          prev.map((item) =>
            item.stockLot.id === params.stockLotId
              ? {
                  ...item,
                  countedQuant: params.countedQuant,
                  validation: validationResult,
                }
              : item
          )
        );
      }

      return validationResult;
    },
    [handleValidateCountedStockLot]
  );

  const value = {
    countedStockLots,
    storeToLocation,
    auditMode,
    isLoading,
    serial,
    messageHistory,
    clearMessageHistory,
    completedStockLots,
    uncompletedStockLots,
    handleUnCompleteStockLot,
    handleDismissStockLot,
    handleResetCounts,
    toggleAuditMode,
    handleApproveStockLot,
    handleUpdateLocation,
    resolveValidation,
    unresolvedValidations,
    deleteValidation,
    handleValidateCountedStockLot,
    validateAndUpdateStockLot,
  };

  return (
    <CountingContext.Provider value={value}>
      {children}
    </CountingContext.Provider>
  );
};

// Custom hook to use the counting context
export const useCountingContext = (): CountingContextType => {
  const context = useContext(CountingContext);

  if (context === undefined) {
    throw new Error(
      'useCountingContext must be used within a CountingProvider'
    );
  }

  return context;
};
